// State code to database table name mapping

/**
 * Map state codes to full state names (for display)
 */
export const STATE_NAMES = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
};

/**
 * Map state codes to database table names for candidates
 * Format: candidates_2026_{state_name_lowercase_with_underscores}
 *
 * Examples:
 * - CA -> candidates_2026_california
 * - NY -> candidates_2026_new_york
 * - NM -> candidates_2026_new_mexico
 */
export const STATE_TO_TABLE_NAME = {
  'AL': 'candidates_2026_alabama',
  'AK': 'candidates_2026_alaska',
  'AZ': 'candidates_2026_arizona',
  'AR': 'candidates_2026_arkansas',
  'CA': 'candidates',  // SPECIAL CASE: California uses just "candidates"
  'CO': 'candidates_2026_colorado',
  'CT': 'candidates_2026_connecticut',
  'DE': 'candidates_2026_delaware',
  'FL': 'candidates_2026_florida',
  'GA': 'candidates_2026_georgia',
  'HI': 'candidates_2026_hawaii',
  'ID': 'candidates_2026_idaho',
  'IL': 'candidates_2026_illinois',
  'IN': 'candidates_2026_indiana',
  'IA': 'candidates_2026_iowa',
  'KS': 'candidates_2026_kansas',
  'KY': 'candidates_2026_kentucky',
  'LA': 'candidates_2026_louisiana',
  'ME': 'candidates_2026_maine',
  'MD': 'candidates_2026_maryland',
  'MA': 'candidates_2026_massachusetts',
  'MI': 'candidates_2026_michigan',
  'MN': 'candidates_2026_minnesota',
  'MS': 'candidates_2026_mississippi',
  'MO': 'candidates_2026_missouri',
  'MT': 'candidates_2026_montana',
  'NE': 'candidates_2026_nebraska',
  'NV': 'candidates_2026_nevada',
  'NH': 'candidates_2026_new_hampshire',
  'NJ': 'candidates_2026_new_jersey',
  'NM': 'candidates_2026_new_mexico',
  'NY': 'candidates_2026_new_york',
  'NC': 'candidates_2026_north_carolina',
  'ND': 'candidates_2026_north_dakota',
  'OH': 'candidates_2026_ohio',
  'OK': 'candidates_2026_oklahoma',
  'OR': 'candidates_2026_oregon',
  'PA': 'candidates_2026_pennsylvania',
  'RI': 'candidates_2026_rhode_island',
  'SC': 'candidates_2026_south_carolina',
  'SD': 'candidates_2026_south_dakota',
  'TN': 'candidates_2026_tennessee',
  'TX': 'candidates_2026_texas',
  'UT': 'candidates_2026_utah',
  'VT': 'candidates_2026_vermont',
  'VA': 'candidates_2026_virginia',
  'WA': 'candidates_2026_washington',
  'WV': 'candidates_2026_west_virginia',
  'WI': 'candidates_2026_wisconsin',
  'WY': 'candidates_2026_wyoming'
};

/**
 * Get the candidates table name for a state code
 * @param {string} stateCode - Two-letter state code (e.g., 'CA', 'NY')
 * @returns {string} Table name (e.g., 'candidates_2026_california')
 */
export function getCandidatesTableName(stateCode) {
  const tableName = STATE_TO_TABLE_NAME[stateCode?.toUpperCase()];
  if (!tableName) {
    throw new Error(`Invalid state code: ${stateCode}`);
  }
  return tableName;
}

/**
 * Get all candidate table names
 * @returns {string[]} Array of all candidate table names
 */
export function getAllCandidateTableNames() {
  return Object.values(STATE_TO_TABLE_NAME);
}

/**
 * Convert state name to table suffix
 * @param {string} stateName - Full state name (e.g., 'New York', 'California')
 * @returns {string} Table suffix (e.g., 'new_york', 'california')
 */
export function stateNameToTableSuffix(stateName) {
  return stateName.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Get state code from table name
 * @param {string} tableName - Table name (e.g., 'candidates_2026_new_york')
 * @returns {string|null} State code (e.g., 'NY') or null if not found
 */
export function getStateCodeFromTableName(tableName) {
  for (const [code, table] of Object.entries(STATE_TO_TABLE_NAME)) {
    if (table === tableName) {
      return code;
    }
  }
  return null;
}
